import { UIActions } from "State/UI/UIActions";
import BaseUISystem from "../../BaseUISystem";
import { IGameState } from "State/Game/IGameState";
import Game from "Game";
import { track } from "Utility/Tracking/Track";
import BaseMenu from "../BaseMenu";
import TextComponent from "Component/Render/Text";
import { RewardType } from "Utility/RewardHelper";
import LevelHelper, { isHardLevel } from "Utility/LevelHelper";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Renderable from "Component/Renderable";
import Timer from "../../../Timer/Timers/Timer";
import { TransactionType } from "Utility/Tracking/DataSet/Transaction";
import { AdContexts } from "Utility/Tracking/DataSet/Ad";
import { GameActions } from "State/Game/GameActions";
import { ContextType } from "State/Game/ContextType";
import { AD_COOLDOWN_TIME, ADS_BEFORE_COOLDOWN, AdsConfig, WATCH_TO_PLAY_LEVEL } from "Constants/Ads";
import { AdvertisementArea } from "Utility/SDK/Advertisement";
import { Anim, StartAnimationClipAsync } from "System/Animation/AnimationHelper";
import { TimerType } from "System/Timer/TimerTypes";
import DateHelper from "Utility/DateHelper";
import Interactable from "Component/Interactable";
import { WorldDataHelper } from "Utility/WorldDataHelper";
import { WinCriteriaType } from "Constants/WinCriteria";
import SpriteComponent from "Component/Render/Sprite";
import TransformComponent from "Component/Transform";
import { ProgressStateHelper } from "State/Player/Progress/ProgressStateHelper";
import { Easing } from "@tweenjs/tween.js";
import { PlaySound } from "System/Audio/AudioHelper";
import { applyDefaultButtonAnimation } from "../../UIHelper";
import { FBTrackingEvent } from "Utility/Tracking/FBTrackingEvent";
import { sleep } from "Utility/async";
import { PreBoosterType } from "State/Card/BoosterType";
import { LevelPopupBooster } from "./LevelPopupBooster";
import { trackFtuxOnHide, trackLevelEndShown, trackLevelStartShown } from "./LevelPopupTrack";
import { trackShownLevelPopup } from "./LevelPopupTrackShown";
import { SideMenu } from "./SideMenu";
import Armature from "Prototype/Armature";
import RelativePositionComponent from "Component/Render/RelativePosition";
import Point from "Utility/Point";
import { DragonbonesDefault, LevelCost } from "../../../Animation/Clip";
import { canShowInterstitialAd } from "State/Level/LevelStateHelper";
import { LevelStatus } from "State/Level/LevelStatus";
import { LevelActions } from "State/Level/LevelActions";
import { PreBoosterTutorial } from "./PreBoosterTutorial";
import { PopupID } from "State/UI/PopupID";
import GameStateHelper from "State/Game/GameStateHelper";
import { isIOS, isNative, Platform } from "Utility/Platform";
import { LevelPopupLayout } from "Layouts/LevelPopup/LevelPopupLayout";
import { EventStateHelper } from "State/Events/EventStateHelper";
import isXmasEvent = EventStateHelper.isXmasEvent;
import getEvent = EventStateHelper.getEvent;
import getEventLayout = EventStateHelper.getEventLayout;

type LevelPopupProps = {
    levelNumber: number;
    fail: boolean;
    selectedPreBoosters?: PreBoosterType[];
    boughtBooster?: PreBoosterType | null;
    skipAnimations?: boolean;
};

export class LevelPopup extends BaseMenu {
    private adTimer: Timer | null = null;
    private levelNumber: number;
    private alreadyCompleted: boolean = false;
    private layout: any;
    private sideMenu: SideMenu;
    private freeLvlArmature: Armature;
    private boosters: LevelPopupBooster[];
    private fail: boolean;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.LEVEL_POPUP, uiSystem, false);

        // TODO: Refactor and replace with generic event atlas.
        if (isXmasEvent(getEvent(Game.store.getState(), PopupID.HARVEST_MENU))) {
            if (!Game.resource.existsOrQueued('xmas_2021')) {
                this.resources.set('xmas_2021', 'resource/graphics/sprite_sheets/xmas_2021.json');
            }
        }
    }

    public onShow(props: LevelPopupProps) {
        super.onShow(props);
        trackShownLevelPopup({ levelNumber: this.levelNumber });

        this.levelNumber = props.levelNumber;
        this.fail = Boolean(props.fail);
        this.fail
            ? trackLevelEndShown({ levelNumber: props.levelNumber })
            : trackLevelStartShown({ levelNumber: props.levelNumber });

        track('tLevelContext', this.levelNumber);
        track('adLevelNum', this.levelNumber);

        const state: IGameState = Game.store.getState();
        const starsAmount: number = ProgressStateHelper.getLevelStars(state, this.levelNumber);
        const levelGoal = WorldDataHelper.getLevelGoal(this.levelNumber);
        const isClearAll = levelGoal[0].type === WinCriteriaType.CLEAR_ALL;

        this.alreadyCompleted = starsAmount > 0;
        this.layout = getEventLayout(state, PopupID.LEVEL_POPUP) ?? new LevelPopupLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.initSideMenu();
        this.initAdTimer();

        this.freeLvlArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'free_lvl_spend',
            armatureName: 'Armature',
            alpha: 1,
            playOnStart: false,
        });
        const armatureTransform: TransformComponent = this.freeLvlArmature.getComponent(TransformComponent);
        armatureTransform.position.set(-10, -4);
        armatureTransform.scale.set(0.75, 0.75);
        this.freeLvlArmature.getComponent(Renderable).visible = false;
        this.layout.costContainer.addChild(this.freeLvlArmature);

        const { titleText, titleIcon, levelText, playFreeContainer, playContainer, titleContainer } = this.layout;
        const { hardLevelText } = this.layout;
        const { playText, costLabel } = this.layout;
        const levelLabel = Game.locale.getText('Level').replace('[x]', this.levelNumber.toString());
        const hardLevelLabel = `${Game.locale.getText('HardLevel')}: ${this.levelNumber.toString()}`;
        const title = this.fail
            ? `${Game.locale.getText('OhNo')} ${Game.locale.getText('TryAgain')}`
            : Game.locale.getText(isClearAll ? 'ClearAllCards' : 'FindGoldCards');

        costLabel.getComponent(TextComponent).text = `${Game.locale.getText('Cost')}:`;
        titleText.getComponent(TextComponent).text = title;
        titleText.getComponent(TextComponent).style.wordWrap = !this.fail;
        titleIcon.getComponent(SpriteComponent).texture = isClearAll ? 'goal_icon' : 'gold_cards_goal_icon';
        titleContainer.getComponent(TransformComponent).y = this.fail ? -120 : -100;
        levelText.getComponent(TextComponent).text = levelLabel;
        hardLevelText.getComponent(TextComponent).text = hardLevelLabel;
        playText.getComponent(TextComponent).text = Game.locale.getText(this.fail ? 'Replay' : 'Play');

        const titleTextTransform = titleText.getComponent(TransformComponent);
        const titleIconTransform = titleIcon.getComponent(TransformComponent);
        titleIconTransform.x = -titleTextTransform.width / 2 - 10;
        titleTextTransform.x = titleIconTransform.width / 2 + 10;

        if (this.levelNumber <= WATCH_TO_PLAY_LEVEL) {
            playFreeContainer.getComponent(Renderable).visible = false;
            playContainer.getComponent(TransformComponent).x = 0;
        }

        const { playFreeButton, playButton, closeButton, character } = this.layout;
        [playFreeButton, playButton, closeButton].forEach(o => applyDefaultButtonAnimation(o));
        playFreeButton.on(GameObjectEvent.SELECTED, this.handleFreePlayClick);
        playButton.on(GameObjectEvent.SELECTED, this.handlePlayClick);
        closeButton.once(GameObjectEvent.SELECTED, this.handleCloseClick);

        character.addComponent(RelativePositionComponent).y = 0;

        const isXmasEventActive = isXmasEvent(getEvent(state, PopupID.LEVEL_POPUP));

        if (this.fail) {
            character.getComponent(SpriteComponent).texture =  isXmasEventActive ? 'Christmas_Girl_FAIL' : 'character_failed_1';
            character.getComponent(TransformComponent).x = -450;
        } else {
            character.getComponent(SpriteComponent).texture = isXmasEventActive ? 'Christmas_Girl_PRELEVEL' : 'character_idle_1';
            character.getComponent(TransformComponent).x = -515;
        }

        this.boosters = [PreBoosterType.REMOVE_THREE, PreBoosterType.JOKER, PreBoosterType.CANISTER].map((type, i) => {
            const booster = new LevelPopupBooster({ type, justBought: props.boughtBooster === type });
            booster.onClick.addListener(() => this.handleBoosterClick(booster));
            booster.root.getComponent(TransformComponent).x = (i - 1) * 200;
            this.layout.boostersContainer.addChild(booster.root);
            booster.isSelected = props.selectedPreBoosters?.includes(type) ?? false;
            booster.update();

            return booster;
        });

        this.layout.failContainers.forEach((container:any) => {
            container.getComponent(Renderable).visible = this.fail;
        });

        if (isHardLevel(this.levelNumber)) {
            this.layout.hardLevelNumberBg.getComponent(Renderable).visible = true;
            this.layout.levelText.getComponent(Renderable).visible = false;

            this.layout.starPlaces.forEach((starPlace:any, i:number) => {
                if (isXmasEventActive) {
                    starPlace.getComponent(Renderable).visible = false;
                } else {
                    starPlace.getComponent(Renderable).visible = true;
                    starPlace.getComponent(TransformComponent).x += [1, 0, -1][i];
                }
            });

            this.layout.stars.forEach((star:any) => {
                star.getComponent(SpriteComponent).texture = 'star_hard';
            });

            if (isXmasEvent(getEvent(state, PopupID.LEVEL_POPUP))) {
                this.layout.headerBg.getComponent(SpriteComponent).texture = 'Christmas_UI_HARD_TOP';
                this.layout.headerBg.getComponent(TransformComponent).x -= 10;
                this.layout.headerBg.getComponent(TransformComponent).y += 10;
            } else {
                this.layout.headerBg.getComponent(SpriteComponent).texture = 'header_hard';
                this.layout.headerBg.getComponent(TransformComponent).y += 24;
            }

            this.layout.closeButton.getComponent(SpriteComponent).texture = 'x_button_hard';
            this.layout.bg.getComponent(SpriteComponent).texture = 'popup_background_hard';
        }

        this.updateStars(starsAmount, this.fail || props.skipAnimations);
        this.updateCost(state);
        this.updateAdButton();
    }

    protected toggleInteractables(value: boolean): void {
        // handles interactables itself
    }

    protected onShown(params?: any): void {
        super.onShown(params);
        PreBoosterTutorial.tryToShowBoostersTutorial({ boosters: this.boosters, parent: this.layout.root });
        if (this.fail) {
            GameStateHelper.trackLevelEnd(Game.store.getState(), false);
        }
    }

    public onDestroy(): void {
        this.adTimer?.stop();
        super.onDestroy();
    }

    private setInputEnabled(value: boolean): void {
        this.layout.root.container.interactiveChildren = value;
    }

    private updateCost(state: IGameState): void {
        const hasFreePlay: boolean = state.playerState.inventory[RewardType.FREE_PLAY] > 0;
        const cost = LevelHelper.getLevelEntryCost(this.levelNumber, this.alreadyCompleted);
        const priceTextComponent = this.layout.priceText.getComponent(TextComponent);

        if (hasFreePlay && cost > 0) {
            priceTextComponent.text = `x${state.playerState.inventory[RewardType.FREE_PLAY]}`;
            this.freeLvlArmature.getComponent(Renderable).visible = true;
            this.freeLvlArmature.container.animation.gotoAndPlayByProgress('idle');
            this.layout.coin.getComponent(Renderable).visible = false;
        } else {
            track('tCoinsLevelEntry', cost);
            priceTextComponent.text = cost === 0 ? Game.locale.getText('Free') : cost.toString();
        }
    }

    private async playLevel(): Promise<void> {
        this.setInputEnabled(false);
        track('transactionType', TransactionType.COIN_PURCHASE);

        if (this.fail) {
            Game.sdk.gameTracking(FBTrackingEvent.TapOnReplay);
            trackFtuxOnHide({ levelNumber: this.levelNumber });

            if (canShowInterstitialAd(Game.store.getState())) {
                this.showInterstitial({ status: LevelStatus.RESTARTING });
                return;
            }
        }

        const restart = this.fail;
        const state: IGameState = Game.store.getState();
        const entryCost = LevelHelper.getLevelEntryCost(this.levelNumber, this.alreadyCompleted);

        const loadLevel = () => {
            GameActions.loadLevel({
                levelNumber: this.levelNumber,
                preBoosters: this.getSelectedPreBoosters(),
                restart,
            });
        };

        if (entryCost > state.playerState.inventory.coins) {
            loadLevel();
            return;
        }

        if (state.playerState.inventory[RewardType.FREE_PLAY] > 0) {
            this.layout.priceText.getComponent(TextComponent).text = `x${state.playerState.inventory[
                RewardType.FREE_PLAY
            ] - 1}`;

            await StartAnimationClipAsync(
                new DragonbonesDefault(this.freeLvlArmature, {
                    animationName: 'spend',
                    progress: 0,
                    playTimes: 1,
                })
            );

            loadLevel();
            return;
        }

        await StartAnimationClipAsync(
            new LevelCost(this.layout.priceText, {
                levelNumber: this.levelNumber,
                alreadyCompleted: this.alreadyCompleted,
            })
        );

        loadLevel();
    }

    private playLevelFree(): void {
        this.setInputEnabled(false);
        track('transactionType', TransactionType.AD);
        track('adContext', AdContexts.FREE_PLAY);

        GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
            placement: AdsConfig.getRewardedWatchToPlay(Game.config),
            payload: {
                g_gameArea: AdvertisementArea.WATCH_TO_PLAY,
                reward: {
                    type: RewardType.FREE_PLAY,
                    value: 1,
                },
                levelPayload: {
                    levelNumber: this.levelNumber,
                    preBoosters: this.getSelectedPreBoosters(),
                },
            },
        });
    }

    private updateAdButton(state: IGameState = Game.store.getState()): void {
        const remainingAdCount: number = state.playerState.progress.adsWatched
            ? ADS_BEFORE_COOLDOWN - state.playerState.progress.adsWatched
            : ADS_BEFORE_COOLDOWN;
        const { adsAmount, playFreeText, playFreeButton } = this.layout;

        // if `time` is null, treat the button as functional and user has ads available
        let time: Date = null;
        let completeTime: Date = null;

        if (!isIOS()) {
            time = state.playerState.timers.has(TimerType.AD_COOLDOWN)
                ? state.playerState.timers.get(TimerType.AD_COOLDOWN)
                : null;
            adsAmount.getComponent(TextComponent).text = (!isNative()) ? "" : `${remainingAdCount}x ${Game.locale.getText('Available')}`;
            if (time) {
                completeTime = new Date(time.getTime() + AD_COOLDOWN_TIME);
            }
        } else {
            adsAmount.getComponent(TextComponent).text = ``;
        }

        (adsAmount as GameObject).getComponent<Renderable>(Renderable).visible = time === null;
        playFreeButton.getComponent(Interactable).enabled = time === null;
        playFreeText.getComponent(TextComponent).text =
            time === null
                ? Game.locale.getText('PlayFree')
                : DateHelper.timeTo(DateHelper.now, completeTime, 'hh:mm:ss');
    }

    private updateStars(starsAmount: number, skipAnimations: boolean): void {
        const sounds = ['3-stars-1', '3-stars-2', '3-stars-3'];

        this.layout.stars.map(async (star:any, i:number) => {
            const visible = i < starsAmount;
            star.getComponent(Renderable).visible = visible;

            if (visible && !skipAnimations) {
                const transform = star.getComponent(TransformComponent);
                transform.scale.set(0);

                await sleep(500 + i * 350);
                Anim(star, { scaleX: 1, scaleY: 1, duration: 450, easing: Easing.Back.Out });
                await sleep(300);
                PlaySound(sounds[i]);
            }
        });
    }

    private handleFreePlayClick = (): void => {
        this.playLevelFree();
    };

    private handlePlayClick = (): void => {
        Game.sdk.gameTracking(FBTrackingEvent.TapOnPlay);
        this.playLevel();
    };

    private handleCloseClick = (): void => {
        this.setInputEnabled(false);
        track('tLevelContext', undefined);
        track('adLevelNum', undefined);
        track('tCoinsLevelEntry', undefined);

        if (this.fail) {
            trackFtuxOnHide({ levelNumber: this.levelNumber });

            if (canShowInterstitialAd(Game.store.getState())) {
                this.showInterstitial({ status: LevelStatus.EXITING });
                return;
            }

            GameActions.endLevel({ status: LevelStatus.EXITING, firstComplete: false });
            return;
        }

        UIActions.hidePopup(this.name);
    };

    private readonly handleBoosterClick = (booster: LevelPopupBooster): void => {
        PlaySound('button-press');
        if (booster.getAmount() === 0) {
            this.openBuyBoosterPopup(booster.type);
        } else {
            booster.isSelected = !booster.isSelected;
            booster.update();
        }
    };

    private openBuyBoosterPopup(type: PreBoosterType): void {
        UIActions.hidePopup(this.name);

        const selectedBoosters = this.getSelectedPreBoosters();

        UIActions.showPopup(PopupID.BUY_BOOSTER_POPUP, {
            type,
            onClose: (bought: boolean): void => {
                bought && selectedBoosters.push(type);

                const props = {
                    levelNumber: this.levelNumber,
                    fail: this.fail,
                    selectedPreBoosters: selectedBoosters,
                    boughtBooster: bought ? type : null,
                    skipAnimations: true,
                };

                if (Game.store.getState().UIState.popupID) {
                    UIActions.queuePopup(PopupID.LEVEL_POPUP, props);
                } else {
                    UIActions.showPopup(PopupID.LEVEL_POPUP, props);
                }
            },
        });
    }

    private getSelectedPreBoosters(): PreBoosterType[] {
        return this.boosters.filter(b => b.isSelected).map(b => b.type);
    }

    /**
     * It also updates adContext tracking
     * */
    private showInterstitial(props: { status: LevelStatus.RESTARTING | LevelStatus.EXITING }): void {
        const state = Game.store.getState();

        track('adContext', AdContexts.WIN_SEQUENCE);

        LevelActions.setLevelState({
            preBoosters: this.getSelectedPreBoosters(),
        });

        GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
            placement: AdsConfig.getInterstitialDefault(Game.config),
            payload: {
                g_gameArea: AdvertisementArea.LEVEL_COMPLETE,
                firstComplete: false,
                status: props.status,
                levelType: state.levelState.levelType,
            },
        });
    }

    private initSideMenu() {
        return;
        if(Game.config.platform === Platform.ANDROID || Game.config.platform === Platform.IOS) return;
        this.sideMenu = new SideMenu({ levelNumber: this.levelNumber });
        this.layout.sideContainer.addChild(this.sideMenu.root);
    }

    private initAdTimer(): void {
        if (!isIOS()) {
            this.adTimer = new Timer(false);
            this.adTimer.onUpdate = this.updateAdButton.bind(this);
            this.adTimer.start();
        }
    }
}
